/**
 * Location store for Idle Depths.
 *
 * Manages location state and travel between locations.
 * Uses Dijkstra's algorithm for pathfinding shortest routes.
 */

import {
  type LocationDefinition,
  type TravelRoute,
  type TravelActivityData,
  type UnlockContext,
  type UnlockResult,
  createIdleActivity,
  createTravelingActivity,
} from '@idle-depths/shared';
import { makeAutoObservable } from 'mobx';

import type { RootStore } from './RootStore';

import { calculateRoute, findCurrentLeg, findFarthestCompletedLocation } from '@/lib/pathfinding';

/**
 * LocationStore manages location state and travel logic.
 *
 * Responsibilities:
 * - Track current location via character data
 * - Calculate routes using Dijkstra's algorithm
 * - Process travel progress each tick
 * - Handle offline travel completion
 */
export class LocationStore {
  /** Reference to root store */
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    } as Record<string, false>);
  }

  // ===========================================================================
  // Computed Properties
  // ===========================================================================

  /**
   * Get the current location definition.
   */
  get currentLocation(): LocationDefinition | undefined {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) {
      return undefined;
    }
    return this.rootStore.gameStore.content.get<LocationDefinition>(
      'locations',
      character.currentLocationId
    );
  }

  /**
   * Get all registered locations.
   */
  get allLocations(): LocationDefinition[] {
    return this.rootStore.gameStore.content.getAll<LocationDefinition>('locations');
  }

  /**
   * Check if currently traveling.
   */
  get isTraveling(): boolean {
    const character = this.rootStore.characterStore.activeCharacter;
    return character?.currentActivity.type === 'traveling';
  }

  /**
   * Get current travel state (null if not traveling).
   */
  get travelState(): TravelActivityData | null {
    const character = this.rootStore.characterStore.activeCharacter;
    if (character?.currentActivity.type !== 'traveling') {
      return null;
    }
    return character.currentActivity.activityData;
  }

  /**
   * Get current travel route (null if not traveling).
   */
  get currentRoute(): TravelRoute | null {
    return this.travelState?.route ?? null;
  }

  /**
   * Get the destination location name (while traveling).
   */
  get destinationName(): string | null {
    if (!this.currentRoute) {
      return null;
    }
    const destination = this.allLocations.find(
      (location) => location.id === this.currentRoute?.toLocationId
    );
    return destination?.name ?? null;
  }

  /**
   * Get the current leg location names (while traveling).
   *
   * Returns an object with "from" and "to" location names for the current leg.
   */
  get currentLegLocations(): { from: string; to: string } | null {
    const state = this.travelState;
    const route = this.currentRoute;
    if (!state || !route) {
      return null;
    }

    const fromId = route.path[state.currentLegIndex];
    const toId = route.path[state.currentLegIndex + 1];

    if (!fromId || !toId) {
      return null;
    }

    const fromLocation = this.allLocations.find((location) => location.id === fromId);
    const toLocation = this.allLocations.find((location) => location.id === toId);

    return {
      from: fromLocation?.name ?? fromId,
      to: toLocation?.name ?? toId,
    };
  }

  // ===========================================================================
  // Location Queries
  // ===========================================================================

  /**
   * Build the unlock context from current game state.
   *
   * This context is passed to location unlock check functions.
   *
   * @returns UnlockContext with current game state, or null if no active character
   */
  private buildUnlockContext(): UnlockContext | null {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) {
      return null;
    }

    // Build bank items map from BankStore
    const bankItems = new Map<string, number>();
    const bankStore = this.rootStore.bankStore;
    for (const [itemId, bankItem] of bankStore.items) {
      bankItems.set(itemId, bankItem.quantity);
    }

    // Get entitlements as array for UnlockContext
    const entitlements = this.rootStore.entitlementsStore.entitlementList;

    // Build visited locations set (track unique locations visited)
    // For now, just include current location. Future: track history.
    const visitedLocations = new Set<string>();
    visitedLocations.add(character.currentLocationId);

    return {
      character,
      bankItems,
      entitlements,
      visitedLocations,
    };
  }

  /**
   * Check if a location is unlocked.
   *
   * If the location has a `checkUnlock` function, it is called with
   * the current game state. Otherwise, data-driven requirements are evaluated.
   *
   * @param locationId - Location ID to check
   * @returns True if unlocked
   */
  isLocationUnlocked(locationId: string): boolean {
    return this.getUnlockResult(locationId).unlocked;
  }

  /**
   * Get the full unlock result for a location.
   *
   * @param locationId - Location ID to check
   * @returns UnlockResult with status and optional reason
   */
  getUnlockResult(locationId: string): UnlockResult {
    const location = this.rootStore.gameStore.content.get<LocationDefinition>(
      'locations',
      locationId
    );

    if (!location) {
      return { unlocked: false, reason: 'Location not found' };
    }

    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) {
      return { unlocked: false, reason: 'No active character' };
    }

    // Starting location is always unlocked
    if (location.isStartingLocation) {
      return { unlocked: true };
    }

    // If location has custom unlock function, use it
    if (location.checkUnlock) {
      const context = this.buildUnlockContext();
      if (!context) {
        return { unlocked: false, reason: 'No active character' };
      }
      return location.checkUnlock(context);
    }

    // Evaluate data-driven requirements
    return this.evaluateUnlockRequirements(location);
  }

  /**
   * Evaluate data-driven unlock requirements.
   *
   * @param location - Location to check
   * @returns UnlockResult based on requirements
   */
  private evaluateUnlockRequirements(location: LocationDefinition): UnlockResult {
    const requirements = location.unlockRequirements;

    // Check DLC requirement
    if (requirements.dlc) {
      const hasEntitlement = this.rootStore.entitlementsStore.hasEntitlement(
        requirements.dlc as import('@idle-depths/shared').EntitlementKey
      );
      if (!hasEntitlement) {
        return { unlocked: false, reason: 'Requires premium content' };
      }
    }

    // Check skill requirements (future - skills not implemented yet)
    if (requirements.skills && Object.keys(requirements.skills).length > 0) {
      // When skills are implemented:
      // for (const [skillId, minLevel] of Object.entries(requirements.skills)) {
      //   if (character.skills[skillId] < minLevel) {
      //     return { unlocked: false, reason: `Requires ${skillId} level ${minLevel}` };
      //   }
      // }
    }

    // Check quest requirements (future - quests not implemented yet)
    if (requirements.quests && requirements.quests.length > 0) {
      // When quests are implemented:
      // for (const questId of requirements.quests) {
      //   if (!character.completedQuests.includes(questId)) {
      //     return { unlocked: false, reason: 'Quest not completed' };
      //   }
      // }
    }

    // Check item requirements
    if (requirements.items && requirements.items.length > 0) {
      const bankStore = this.rootStore.bankStore;
      for (const itemId of requirements.items) {
        const bankItem = bankStore.items.get(itemId);
        if (!bankItem || bankItem.quantity <= 0) {
          return { unlocked: false, reason: `Requires item: ${itemId}` };
        }
      }
    }

    // All requirements met (or no requirements)
    return { unlocked: true };
  }

  /**
   * Calculate route between two locations.
   *
   * @param fromId - Starting location ID
   * @param toId - Destination location ID
   * @returns Route or null if no path exists
   */
  calculateRoute(fromId: string, toId: string): TravelRoute | null {
    return calculateRoute(this.allLocations, fromId, toId);
  }

  /**
   * Get a location definition by ID.
   *
   * @param locationId - Location ID
   * @returns Location definition or undefined
   */
  getLocation(locationId: string): LocationDefinition | undefined {
    return this.allLocations.find((location) => location.id === locationId);
  }

  // ===========================================================================
  // Travel Actions
  // ===========================================================================

  /**
   * Start traveling to a location.
   *
   * @param toLocationId - Destination location ID
   * @throws Error if no path exists or player already at location
   */
  startTravel(toLocationId: string): void {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) {
      throw new Error('No active character');
    }

    // Can't travel if already there
    if (character.currentLocationId === toLocationId) {
      throw new Error('Already at destination');
    }

    // Can't travel if already traveling
    if (this.isTraveling) {
      throw new Error('Already traveling');
    }

    // Calculate route
    const route = this.calculateRoute(character.currentLocationId, toLocationId);
    if (!route) {
      throw new Error('No path to destination');
    }

    // Create travel state
    const now = Date.now();
    const travelData: TravelActivityData = {
      route,
      startedAt: now,
      endsAt: now + route.totalTimeMs,
      currentLegIndex: 0,
      currentLegProgress: 0,
    };

    // Set character activity
    const activity = createTravelingActivity(travelData);
    this.rootStore.characterStore.setActivity(activity);
  }

  /**
   * Cancel ongoing travel.
   *
   * Character stays at the last fully completed location in their journey.
   */
  cancelTravel(): void {
    if (!this.isTraveling || !this.travelState) {
      return;
    }

    // Find farthest completed location
    const now = Date.now();
    const elapsed = now - this.travelState.startedAt;
    const farthestLocation = findFarthestCompletedLocation(this.travelState.route, elapsed);

    // Update location to farthest completed
    this.rootStore.characterStore.updateCurrentLocation(farthestLocation);

    // Set to idle
    this.rootStore.characterStore.setActivity(createIdleActivity());

    // Show notification
    this.rootStore.notificationStore.addToast({
      type: 'info',
      title: 'Travel Cancelled',
      message: 'You stopped traveling',
    });
  }

  // ===========================================================================
  // Tick Processing
  // ===========================================================================

  /**
   * Process travel progress (called by tick system).
   *
   * Updates travel progress and completes travel if destination reached.
   *
   * @param _deltaMs - Time elapsed since last tick (unused, we use timestamps)
   */
  tick(_deltaMs: number): void {
    if (!this.isTraveling || !this.travelState) {
      return;
    }

    const now = Date.now();
    const state = this.travelState;

    // Check if travel completed
    if (now >= state.endsAt) {
      this.completeTravel();
      return;
    }

    // Calculate progress
    const elapsed = now - state.startedAt;
    const totalProgress = elapsed / state.route.totalTimeMs;

    // Update current leg tracking
    const { legIndex, legProgress } = findCurrentLeg(state.route, elapsed);

    // Update activity with new progress and leg data through CharacterStore
    // This ensures MobX properly tracks the changes
    this.rootStore.characterStore.updateTravelProgress(totalProgress, legIndex, legProgress);
  }

  /**
   * Process offline travel.
   *
   * Called when player logs in to calculate travel progress while offline.
   * If travel completed while offline, completes it. Otherwise, updates
   * the leg tracking to reflect current progress.
   *
   * Note: We do NOT update currentLocationId during travel - the character's
   * position along the route is tracked via currentLegIndex. The location
   * only updates when travel completes.
   */
  processOfflineTravel(): void {
    if (!this.isTraveling || !this.travelState) {
      return;
    }

    const now = Date.now();
    const state = this.travelState;

    // If travel completed while offline, complete it
    if (now >= state.endsAt) {
      this.completeTravel();
      return;
    }

    // Travel still in progress - update leg tracking to reflect where we are
    const elapsed = now - state.startedAt;
    const totalProgress = elapsed / state.route.totalTimeMs;
    const { legIndex, legProgress } = findCurrentLeg(state.route, elapsed);

    // Update through CharacterStore for proper MobX tracking
    this.rootStore.characterStore.updateTravelProgress(totalProgress, legIndex, legProgress);
  }

  // ===========================================================================
  // Private Methods
  // ===========================================================================

  /**
   * Complete travel and arrive at destination.
   */
  private completeTravel(): void {
    const state = this.travelState;
    if (!state) {
      return;
    }

    const destination = state.route.toLocationId;
    const location = this.getLocation(destination);

    // Update location
    this.rootStore.characterStore.updateCurrentLocation(destination);

    // Set to idle
    this.rootStore.characterStore.setActivity(createIdleActivity());

    // Show arrival notification
    this.rootStore.notificationStore.addToast({
      type: 'success',
      title: 'Arrived',
      message: `Welcome to ${location?.name ?? destination}`,
    });
  }

  // ===========================================================================
  // Store Lifecycle
  // ===========================================================================

  /**
   * Reset store to initial state.
   */
  reset(): void {
    // No internal state to reset - all state is in character data
  }
}
